import React, { useState } from "react";
import { Plus, Book } from "lucide-react";
import { Button } from "@shared/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@shared/ui/alert-dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { GenericTable } from "@shared/components/GenericTable";
import { useSopTabDetails, useDeleteSopReading } from "../../../../hooks";
import { AddReadingModal, EditReadingModal } from "../modals/ReadingModal";
import { useToast } from "@shared/hooks/use-toast";

interface ReadingsTabProps {
  sopDetails: any;
}

interface Reading {
  name: string;
  unit: string;
  minimum?: string;
  maximum?: string;
  is_photo_req?: string;
}

const ReadingsTab: React.FC<ReadingsTabProps> = ({ sopDetails }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedReading, setSelectedReading] = useState<Reading | null>(null);
  const [selectedReadingIndex, setSelectedReadingIndex] = useState<number>(-1);
  // Add this state for delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [readingToDelete, setReadingToDelete] = useState<{
    index: number;
    reading: Reading;
  } | null>(null);

  // Add the delete mutation hook
  const deleteReadingMutation = useDeleteSopReading();
  const { toast } = useToast();

  // Fetch reading data using the hook
  const {
    data: readingData,
    isLoading: readingsLoading,
    error: readingsError,
  } = useSopTabDetails({
    id: sopDetails.id.toString(),
    remoteUtilityId: sopDetails.remoteUtilityId?.toString(),
    fieldName: "readings",
  });

  // Event handlers
  const handleAddReading = () => {
    setIsAddModalOpen(true);
  };

  const handleEditReading = (readingIndex: number, reading: Reading) => {
    setSelectedReading(reading);
    setSelectedReadingIndex(readingIndex);
    setIsEditModalOpen(true);
  };

  const handleDeleteReading = (readingIndex: number, reading: Reading) => {
    setReadingToDelete({ index: readingIndex, reading });
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteReading = async () => {
    if (!readingToDelete) return;

    try {
      await deleteReadingMutation.mutateAsync({
        sopId: sopDetails.id.toString(),
        readingName: readingToDelete.reading.name,
      });

      setDeleteConfirmOpen(false);
      setReadingToDelete(null);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedReading(null);
    setSelectedReadingIndex(-1);
  };

  // Extract readings from API response and add index for row identification
  const readings: (Reading & { index: number })[] = (
    readingData?.result?.readings || []
  ).map((reading: Reading, index: number) => ({
    ...reading,
    index,
  }));

  // GenericTable column configuration
  const tableColumns = [
    {
      key: "index",
      header: "Sr. No",
      render: (value: number) => (
        <span className="font-medium">{value + 1}</span>
      ),
    },
    {
      key: "name",
      header: "Reading Name",
      sortable: true,
      render: (value: string) => <span className="text-gray-900">{value}</span>,
    },
    {
      key: "unit",
      header: "Unit",
      sortable: true,
      render: (value: string) => <span className="text-gray-700">{value}</span>,
    },
    {
      key: "minimum",
      header: "Min",
      sortable: true,
      render: (value: string) => (
        <span className="text-gray-700">{value ?? "-"}</span>
      ),
    },
    {
      key: "maximum",
      header: "Max",
      sortable: true,
      render: (value: string) => (
        <span className="text-gray-700">{value ?? "-"}</span>
      ),
    },
    {
      key: "isPhotoReq",
      header: "Photo Required",
      type: "badge" as const,
      badgeConfig: {
        getClassName: (value: string) =>
          value === "Yes" ? "" : "bg-secondary",
        getVariant: (value: string) =>
          value === "Yes" ? "default" : "secondary",
      },
      render: (value: string) =>
        value === "Yes" ? "Yes" : value === "No" ? "No" : "Not Set",
    },
  ];

  // GenericTable actions configuration
  const tableActions = [
    {
      label: "Edit Reading",
      onClick: (row: Reading & { index: number }) =>
        handleEditReading(row.index, row),
      icon: "Pencil" as const,
      variant: "ghost" as const,
    },
    {
      label: "Delete Reading",
      onClick: (row: Reading & { index: number }) =>
        handleDeleteReading(row.index, row),
      icon: "Trash2" as const,
      variant: "ghost" as const,
      className: "text-destructive hover:text-destructive",
    },
  ];

  return (
    <>
      <div className="mt-4 space-y-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center pb-2">
                <Book className="mr-2 h-5 w-5 text-primary" />
                Required Readings
              </CardTitle>
              <CardDescription>
                Readings to be captured during this procedure
              </CardDescription>
            </div>
            <Button onClick={handleAddReading}>
              <Plus className="mr-2 h-4 w-4" />
              Add
            </Button>
          </CardHeader>
          <CardContent>
            <GenericTable
              data={readings}
              columns={tableColumns}
              rowKey="index"
              actions={tableActions}
              actionsType="dropdown"
              emptyMessage="No readings defined for this SOP"
            />
          </CardContent>
        </Card>
      </div>
      {/* Add Reading Modal */}
      <AddReadingModal
        isOpen={isAddModalOpen}
        onClose={handleCloseAddModal}
        sopId={sopDetails.id.toString()}
      />
      {/* Edit Reading Modal */}
      <EditReadingModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        sopId={sopDetails.id.toString()}
        reading={selectedReading}
        readingIndex={selectedReadingIndex}
      />

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Reading</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the reading "
              {readingToDelete?.reading.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteReading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteReadingMutation.isPending}
            >
              {deleteReadingMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ReadingsTab;
